/**
 * My Works: every proposal and publication you write with others, across all your consortia, most recently
 * edited by anyone on the team first. The same list as the app bar's "My Works" menu, with room to search and filter.
 * Backend: GET /proposal/proposals/ (only documents of consortia you belong to).
 */
import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Avatar, AvatarGroup, Box, Chip, CircularProgress, Divider, InputAdornment, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { fetcher } from 'shared/helpers/axios';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader, { HEADER_TABS_SX } from 'components/common/layout/PageHeader';

const KINDS = [['all', 'All'], ['publication', 'Publications'], ['proposal', 'Proposals']];

function edited(iso) {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
  if (days <= 0) return 'edited today';
  if (days === 1) return 'edited yesterday';
  if (days < 30) return `edited ${days} days ago`;
  return `edited ${new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

export default function WorksPage() {
  const navigate = useNavigate();
  const { data, error, isLoading } = useSWR('/proposal/proposals/?page_size=500', fetcher);
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');
  const all = useMemo(() => {
    const rows = (Array.isArray(data) ? data : data?.results || []).filter((w) => !w.you_left);   // left: not yours
    const at = (w) => new Date(w.last_edited || w.updated || 0);
    return [...rows].sort((a, b) => at(b) - at(a));
  }, [data]);
  const count = (k) => (k === 'all' ? all.length : all.filter((w) => (w.document_type || 'proposal') === k).length);
  const words = search.trim().toLowerCase();
  const shown = all.filter((w) => (kind === 'all' || (w.document_type || 'proposal') === kind)
    && (!words || [w.title, w.acronym, w.consortium_name, w.target_journal, w.call_title]
      .some((v) => (v || '').toLowerCase().includes(words))));

  return (
    <PageContainer>
      {/* tabs after the title and the search at the right: the Collaboration Hub's header */}
      <PageHeader title="Works" subtitle="Proposals and publications you are writing with your consortia"
        actions={(
          <TextField size="small" placeholder="Search titles, consortia, journals…" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ width: 280 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        )}>
        <Tabs value={kind} onChange={(_e, v) => setKind(v)} variant="scrollable" scrollButtons="auto"
          sx={{ ...HEADER_TABS_SX, minWidth: 0 }}>
          {KINDS.map(([k, label]) => <Tab key={k} value={k} label={`${label} (${count(k)})`} />)}
        </Tabs>
      </PageHeader>

      {/* the list as on the notifications page: a rule under the header, rows separated by lines */}
      {/* <Divider /> */}
      <Box sx={{ p: 1.5 }}>
        {isLoading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ m: 2 }}>Your works could not be loaded.</Alert>}
        {!isLoading && !error && shown.length === 0 && (
          <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary', fontSize: 14 }}>
            {all.length ? 'Nothing matches this search.' : 'No works yet. Start a proposal or a publication from one of your consortia.'}
          </Typography>
        )}
        {shown.map((w) => {
          const isPub = w.document_type === 'publication';
          const title = (w.title || '').trim() || 'Untitled';
          return (
            <Box key={w.id} role="link" tabIndex={0}
              onClick={() => navigate(`/consortium/${w.consortium}/${isPub ? 'publications' : 'proposals'}/${w.id}/`)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.click(); }}
              sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', px: 2.5, py: 1.75, cursor: 'pointer', bgcolor: 'background.paper',
                borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 },
                '&:hover, &:focus-visible': { bgcolor: 'action.hover', outline: 'none' } }}>
              <Box flex={1} minWidth={0}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  {w.acronym && <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{w.acronym}</Typography>}
                  <Chip size="small" label={isPub ? 'Publication' : 'Proposal'} color={isPub ? 'secondary' : 'primary'}
                    variant="outlined" sx={{ height: 20, fontSize: 11.5 }} />
                  {w.status && <Chip size="small" label={w.status.replace(/_/g, ' ')} sx={{ height: 20, fontSize: 11.5, textTransform: 'capitalize' }} />}
                </Box>
                <Typography sx={{ fontSize: 14.5, mt: 0.25 }} noWrap>{title}</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }} noWrap>
                  {[w.consortium_name, isPub ? w.target_journal : w.call_title,
                    w.section_count ? `${w.section_count} sections` : ''].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between',
                alignSelf: 'stretch', gap: 1, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap', pt: 0.25 }}>{edited(w.last_edited || w.updated)}</Typography>
                {w.coauthors?.total > 0 && (
                  <Tooltip title={`With ${w.coauthors.people.map((c) => c.name).filter(Boolean).join(', ')}`
                    + (w.coauthors.total > w.coauthors.people.length ? ` and ${w.coauthors.total - w.coauthors.people.length} more` : '')}>
                    <AvatarGroup max={6} total={w.coauthors.total}
                      sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: 10.5, fontWeight: 600, borderWidth: 1.5,
                        bgcolor: 'primary.light', color: 'primary.contrastText' } }}>
                      {w.coauthors.people.map((c, i) => (
                        <Avatar key={c.id || `${c.name}-${i}`} alt={c.name} src={c.avatar_url || undefined}>
                          {(c.name || '?')[0]}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </PageContainer>
  );
}
